import React from 'react';
import { UseMapVisualizationReturn } from '../../../hooks/useMapVisualization';

interface AdvancedVisualizationControlsProps {
  mapVisualization: UseMapVisualizationReturn;
}

/**
 * AdvancedVisualizationControls - Advanced controls for heatmap and grid visualizations
 * Extracted from MapsTab to be reusable across components
 */
const AdvancedVisualizationControls: React.FC<AdvancedVisualizationControlsProps> = ({
  mapVisualization,
}) => {
  const {
    visualizationType,
    filters,
    statTypeVisualizationMap,
    heatmapRadius,
    setHeatmapRadius,
    heatmapSmoothness,
    setHeatmapSmoothness,
    heatmapOpacity,
    setHeatmapOpacity,
    gridSize,
    setGridSize,
    gridOpacity,
    setGridOpacity,
    gridShowLabels,
    setGridShowLabels,
  } = mapVisualization;

  // Check if heatmap is active - only check per-stat mappings when they're active
  const hasHeatmap = (() => {
    // If per-stat-type mapping is active, only check per-stat mappings
    if (filters.stat_type_id && filters.stat_type_id.length > 0) {
      return filters.stat_type_id.some(id => {
        const vizTypes = statTypeVisualizationMap[id] || ['scatter'];
        return vizTypes.includes('heatmap');
      });
    }
    // Otherwise, check global visualization type
    return visualizationType.includes('heatmap');
  })();

  // Check if grid is active - only check per-stat mappings when they're active
  const hasGrid = (() => {
    // If per-stat-type mapping is active, only check per-stat mappings
    if (filters.stat_type_id && filters.stat_type_id.length > 0) {
      return filters.stat_type_id.some(id => {
        const vizTypes = statTypeVisualizationMap[id] || ['scatter'];
        return vizTypes.includes('grid');
      });
    }
    // Otherwise, check global visualization type
    return visualizationType.includes('grid');
  })();

  // Don't render anything if neither heatmap nor grid is active
  if (!hasHeatmap && !hasGrid) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-4 items-center pt-3 border-t border-gray-200 mt-3">
      {/* Heatmap Controls - Show when heatmap view is selected OR when per-stat mapping has heatmap */}
      {hasHeatmap && (
        <>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Radius:</label>
            <input
              type="range"
              min="50"
              max="200"
              step="10"
              value={heatmapRadius}
              onChange={(e) => setHeatmapRadius(Number(e.target.value))}
              className="w-24"
            />
            <span className="text-sm text-gray-600 w-12">{heatmapRadius}px</span>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Smoothness:</label>
            <input
              type="range"
              min="1"
              max="6"
              step="0.5"
              value={heatmapSmoothness}
              onChange={(e) => setHeatmapSmoothness(Number(e.target.value))}
              className="w-24"
            />
            <span className="text-sm text-gray-600 w-8">{heatmapSmoothness.toFixed(1)}</span>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Heatmap Opacity:</label>
            <input
              type="range"
              min="0.1"
              max="1"
              step="0.1"
              value={heatmapOpacity}
              onChange={(e) => setHeatmapOpacity(Number(e.target.value))}
              className="w-24"
            />
            <span className="text-sm text-gray-600 w-8">{Math.round(heatmapOpacity * 100)}%</span>
          </div>
        </>
      )}

      {/* Grid Controls - Show when grid view is selected OR when per-stat mapping has grid */}
      {hasGrid && (
        <>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Grid Size:</label>
            <input
              type="range"
              min="5"
              max="20"
              step="1"
              value={gridSize}
              onChange={(e) => setGridSize(Number(e.target.value))}
              className="w-24"
            />
            <span className="text-sm text-gray-600 w-12">{gridSize}x{gridSize}</span>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Grid Opacity:</label>
            <input
              type="range"
              min="0.1"
              max="1"
              step="0.1"
              value={gridOpacity}
              onChange={(e) => setGridOpacity(Number(e.target.value))}
              className="w-24"
            />
            <span className="text-sm text-gray-600 w-8">{Math.round(gridOpacity * 100)}%</span>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Show Labels:</label>
            <button
              type="button"
              onClick={() => setGridShowLabels(!gridShowLabels)}
              className={`
                relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                ${gridShowLabels ? 'bg-blue-600' : 'bg-gray-200'}
              `}
              role="switch"
              aria-checked={gridShowLabels}
            >
              <span
                className={`
                  pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
                  ${gridShowLabels ? 'translate-x-5' : 'translate-x-0'}
                `}
              />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AdvancedVisualizationControls;

